import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Stack,
  Card,
  Badge,
  Text,
  Grid,
  ActionIcon,
  Alert,
  Modal,
  Textarea,
  Progress,
  Tabs,
  Timeline,
  Slider,
  Switch,
  NumberInput,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconZoomIn,
  IconCamera,
  IconUser,
  IconTarget,
  IconAdjustments,
  IconArrowLeft,
  IconPlayerPlay,
  IconDownload,
  IconShare,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useDetection, useVerifyDetection, useUpdateDetectionSettings } from '../../hooks/useDetections';

const DetectionVerification: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [verificationNotes, setVerificationNotes] = useState('');
  const [confidence, setConfidence] = useState(0);
  // const [adjustedBoundingBox] = useState<any>(null);
  const [saveForTraining, setSaveForTraining] = useState(false);

  const [imageModalOpened, { open: openImageModal, close: closeImageModal }] = useDisclosure(false);
  const [settingsModalOpened, { open: openSettingsModal, close: closeSettingsModal }] = useDisclosure(false);

  const {
    data: detection,
    isLoading,
    error,
  } = useDetection(id || '');

  const verifyDetectionMutation = useVerifyDetection();
  const updateSettingsMutation = useUpdateDetectionSettings();

  const handleVerifyDetection = async (status: 'verified' | 'rejected' | 'needs_review') => {
    if (!detection) return;

    try {
      await verifyDetectionMutation.mutateAsync({
        id: detection.id,
        status: status,
        notes: verificationNotes,
      });

      notifications.show({
        title: 'Success',
        message: `Detection ${status === 'verified' ? 'verified' : status === 'rejected' ? 'rejected' : 'marked for review'}`,
        color: status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'orange',
      });

      navigate('/admin/activities');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update detection status',
        color: 'red',
      });
    }
  };

  const handleUpdateSettings = async (settings: any) => {
    if (!detection) return;

    try {
      await updateSettingsMutation.mutateAsync({
        id: detection.id,
        settings,
      });

      notifications.show({
        title: 'Success',
        message: 'Detection settings updated',
        color: 'green',
      });

      closeSettingsModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update settings',
        color: 'red',
      });
    }
  };

  const getActivityTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'entry': 'green',
      'exit': 'blue',
      'play': 'violet',
      'sleep': 'indigo',
      'meal': 'orange',
      'safety_concern': 'red',
      'incident': 'red',
      'unknown': 'gray',
    };
    return colors[type] || 'gray';
  };

  const getVerificationStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'verified': 'green',
      'rejected': 'red',
      'needs_review': 'orange',
      'pending': 'blue',
    };
    return colors[status] || 'gray';
  };

  if (isLoading) {
    return <div>Loading detection details...</div>;
  }

  if (error || !detection) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error loading detection"
        color="red"
      >
        Failed to load detection details. Please try again.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Group>
          <ActionIcon variant="subtle" onClick={() => navigate('/admin/activities')}>
            <IconArrowLeft size={16} />
          </ActionIcon>
          <div>
            <Title order={2}>Detection Verification</Title>
            <Text size="sm" c="dimmed">
              ID: {detection.id} • {detection.detected_at ? new Date(detection.detected_at).toLocaleString() : 'Unknown time'}
            </Text>
          </div>
        </Group>
        <Group>
          <Badge color={getActivityTypeColor(detection.activity_type || '')} size="lg">
            {detection.activity_type?.replace('_', ' ') || 'Unknown'}
          </Badge>
          <Badge color={getVerificationStatusColor(detection.verification_status || '')} size="lg">
            {detection.verification_status?.replace('_', ' ') || 'Pending'}
          </Badge>
        </Group>
      </Group>

      <Grid>
        {/* Main Content */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {/* Detection Image/Video */}
            <Card>
              <Group justify="space-between" mb="md">
                <Title order={4}>Detection Evidence</Title>
                <Group>
                  <ActionIcon variant="outline" onClick={openImageModal}>
                    <IconZoomIn size={16} />
                  </ActionIcon>
                  <ActionIcon variant="outline">
                    <IconDownload size={16} />
                  </ActionIcon>
                  <ActionIcon variant="outline">
                    <IconShare size={16} />
                  </ActionIcon>
                </Group>
              </Group>

              {/* Placeholder for image/video - in real implementation, this would show the actual detection */}
              <Paper withBorder p="xl" style={{ textAlign: 'center', backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <IconCamera size={48} color="gray" />
                <Text size="sm" c="dimmed" mt="md">
                  Detection image/video placeholder
                </Text>
                <Text size="xs" c="dimmed">
                  Frame: {detection.timestamp}
                </Text>

                {/* Bounding box visualization placeholder */}
                {detection.bounding_box && (
                  <div style={{
                    marginTop: '16px',
                    padding: '8px',
                    border: '2px dashed var(--mantine-color-blue-6)',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    <Text size="xs" c="blue">
                      Bounding Box: {JSON.stringify(detection.bounding_box)}
                    </Text>
                  </div>
                )}
              </Paper>

              {/* Confidence Score */}
              <Group justify="space-between" mt="md">
                <Text size="sm" fw={500}>AI Confidence</Text>
                <Group gap="xs">
                  <Progress
                    value={(detection.confidence || 0) * 100}
                    color={(detection.confidence || 0) > 0.8 ? 'green' : (detection.confidence || 0) > 0.6 ? 'yellow' : 'red'}
                    style={{ width: 100 }}
                  />
                  <Text size="sm" fw={500}>
                    {((detection.confidence || 0) * 100).toFixed(1)}%
                  </Text>
                </Group>
              </Group>
            </Card>

            {/* Detection Details */}
            <Card>
              <Title order={4} mb="md">Detection Analysis</Title>

              <Tabs defaultValue="overview">
                <Tabs.List>
                  <Tabs.Tab value="overview">Overview</Tabs.Tab>
                  <Tabs.Tab value="technical">Technical Data</Tabs.Tab>
                  <Tabs.Tab value="context">Context</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview" pt="md">
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text size="sm">Student:</Text>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {detection.student ?
                            `${detection.student.first_name} ${detection.student.last_name}` :
                            'Unknown Student'
                          }
                        </Text>
                        {detection.student && (
                          <ActionIcon size="xs" variant="subtle">
                            <IconUser size={12} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Group>

                    <Group justify="space-between">
                      <Text size="sm">Camera:</Text>
                      <Text size="sm" fw={500}>
                        {detection.camera?.name || 'Unknown Camera'}
                      </Text>
                    </Group>

                    <Group justify="space-between">
                      <Text size="sm">Location:</Text>
                      <Text size="sm" fw={500}>
                        {detection.camera?.location?.area?.name || 'Unknown Area'}
                      </Text>
                    </Group>

                    <Group justify="space-between">
                      <Text size="sm">Safety Score:</Text>
                      <Badge color={(detection.safety_score || 0) > 70 ? 'green' : (detection.safety_score || 0) > 40 ? 'orange' : 'red'}>
                        {detection.safety_score || 'N/A'}
                      </Badge>
                    </Group>
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="technical" pt="md">
                  <Stack gap="md">
                    <div>
                      <Text size="sm" mb="xs">Bounding Box Coordinates:</Text>
                      <pre style={{
                        fontSize: '12px',
                        background: 'var(--mantine-color-gray-0)',
                        padding: '8px',
                        borderRadius: '4px',
                        overflow: 'auto'
                      }}>
                        {detection.bounding_box ? JSON.stringify(detection.bounding_box, null, 2) : 'Not available'}
                      </pre>
                    </div>

                    {detection.metadata && (
                      <div>
                        <Text size="sm" mb="xs">Additional Metadata:</Text>
                        <pre style={{
                          fontSize: '12px',
                          background: 'var(--mantine-color-gray-0)',
                          padding: '8px',
                          borderRadius: '4px',
                          overflow: 'auto'
                        }}>
                          {JSON.stringify(detection.metadata, null, 2)}
                        </pre>
                      </div>
                    )}

                    <Group justify="space-between">
                      <Text size="sm">Model Version:</Text>
                      <Text size="sm" fw={500}>v1.2.3</Text>
                    </Group>

                    <Group justify="space-between">
                      <Text size="sm">Processing Time:</Text>
                      <Text size="sm" fw={500}>0.25s</Text>
                    </Group>
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="context" pt="md">
                  <Stack gap="md">
                    <Timeline active={1} bulletSize={24} lineWidth={2}>
                      <Timeline.Item
                        bullet={<IconCamera size={12} />}
                        title="Detection Triggered"
                      >
                        <Text size="xs" c="dimmed">
                          AI model detected activity at {detection.timestamp ? new Date(detection.timestamp).toLocaleTimeString() : 'Unknown time'}
                        </Text>
                      </Timeline.Item>

                      <Timeline.Item
                        bullet={<IconTarget size={12} />}
                        title="Face Recognition"
                      >
                        <Text size="xs" c="dimmed">
                          Student identification: {(detection.confidence || 0) > 0.8 ? 'High confidence' : 'Low confidence'}
                        </Text>
                      </Timeline.Item>

                      <Timeline.Item
                        bullet={<IconAlertTriangle size={12} />}
                        title="Safety Assessment"
                      >
                        <Text size="xs" c="dimmed">
                          Safety score calculated: {detection.safety_score || 'Pending'}
                        </Text>
                      </Timeline.Item>
                    </Timeline>
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Card>
          </Stack>
        </Grid.Col>

        {/* Verification Panel */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            {/* Verification Actions */}
            {detection.verification_status === 'pending' && (
              <Card>
                <Title order={4} mb="md">Verification</Title>

                <Stack gap="md">
                  <Textarea
                    label="Verification Notes"
                    placeholder="Add notes about this detection..."
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.currentTarget.value)}
                    autosize
                    minRows={3}
                    maxRows={6}
                  />

                  <div>
                    <Text size="sm" mb="xs">Confidence Override</Text>
                    <Slider
                      value={confidence}
                      onChange={setConfidence}
                      min={0}
                      max={100}
                      step={5}
                      marks={[
                        { value: 0, label: 'Auto' },
                        { value: 50, label: '50%' },
                        { value: 100, label: '100%' }
                      ]}
                    />
                  </div>

                  <Switch
                    label="Save for Training"
                    description="Include this detection in future model training"
                    checked={saveForTraining}
                    onChange={(e) => setSaveForTraining(e.currentTarget.checked)}
                  />

                  <Group>
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={() => handleVerifyDetection('verified')}
                      loading={verifyDetectionMutation.isPending}
                      fullWidth
                    >
                      Verify
                    </Button>
                    <Button
                      color="red"
                      leftSection={<IconX size={16} />}
                      onClick={() => handleVerifyDetection('rejected')}
                      loading={verifyDetectionMutation.isPending}
                      fullWidth
                    >
                      Reject
                    </Button>
                  </Group>

                  <Button
                    color="orange"
                    leftSection={<IconAlertTriangle size={16} />}
                    onClick={() => handleVerifyDetection('needs_review')}
                    loading={verifyDetectionMutation.isPending}
                    variant="outline"
                    fullWidth
                  >
                    Needs Review
                  </Button>
                </Stack>
              </Card>
            )}

            {/* Already Verified */}
            {detection.verification_status !== 'pending' && (
              <Card>
                <Title order={4} mb="md">Verification Status</Title>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text size="sm">Status:</Text>
                    <Badge color={getVerificationStatusColor(detection.verification_status || 'pending')}>
                      {detection.verification_status?.replace('_', ' ')}
                    </Badge>
                  </Group>

                  {detection.verified_at && (
                    <Group justify="space-between">
                      <Text size="sm">Verified At:</Text>
                      <Text size="sm" fw={500}>
                        {new Date(detection.verified_at).toLocaleString()}
                      </Text>
                    </Group>
                  )}

                  {detection.verification_notes && (
                    <div>
                      <Text size="sm" mb="xs">Notes:</Text>
                      <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                        {detection.verification_notes}
                      </Text>
                    </div>
                  )}
                </Stack>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <Title order={4} mb="md">Quick Actions</Title>
              <Stack gap="xs">
                <Button variant="outline" leftSection={<IconPlayerPlay size={16} />} fullWidth>
                  View Video Context
                </Button>
                <Button variant="outline" leftSection={<IconUser size={16} />} fullWidth>
                  View Student Profile
                </Button>
                <Button variant="outline" leftSection={<IconCamera size={16} />} fullWidth>
                  View Camera Details
                </Button>
                <Button
                  variant="outline"
                  leftSection={<IconAdjustments size={16} />}
                  onClick={openSettingsModal}
                  fullWidth
                >
                  Adjust Detection Settings
                </Button>
              </Stack>
            </Card>

            {/* Related Detections */}
            <Card>
              <Title order={4} mb="md">Related Detections</Title>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  • 3 similar detections in the last hour
                </Text>
                <Text size="xs" c="dimmed">
                  • Same student detected 5 times today
                </Text>
                <Text size="xs" c="dimmed">
                  • 12 total detections from this camera today
                </Text>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Image Modal */}
      <Modal
        opened={imageModalOpened}
        onClose={closeImageModal}
        title="Detection Image"
        size="lg"
      >
        <Paper withBorder p="xl" style={{ textAlign: 'center' }}>
          <IconCamera size={64} color="gray" />
          <Text c="dimmed" mt="md">
            Full-size detection image would appear here
          </Text>
        </Paper>
      </Modal>

      {/* Settings Modal */}
      <Modal
        opened={settingsModalOpened}
        onClose={closeSettingsModal}
        title="Detection Settings"
        size="md"
      >
        <Stack gap="md">
          <NumberInput
            label="Confidence Threshold"
            placeholder="0.8"
            min={0}
            max={1}
            step={0.1}
            defaultValue={0.8}
          />
          <Switch
            label="Enable Auto-Verification"
            description="Automatically verify high-confidence detections"
          />
          <Switch
            label="Training Data Collection"
            description="Collect this type of detection for model training"
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={closeSettingsModal}>
              Cancel
            </Button>
            <Button onClick={() => handleUpdateSettings({})}>
              Save Settings
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default DetectionVerification;
